(ns ductile.ui.paneling
  (:require [ductile.ui.components :as ui]
            [nextjournal.clerk.render.hooks :as hooks]))

(def default-splits
  {:col 0.67
   :left 0.67
   :right 0.67})

(def focus->default-splits
  {:vehicles (assoc default-splits :left 0.5)
   :transports (assoc default-splits :left 0.5)
   :map default-splits})

(def focus->default-secondary
  {:vehicles :transports
   :transports :vehicles
   :map :vehicles})

(defn clamp [v min-v max-v]
  (-> v (max min-v) (min max-v)))

(defn clamp-splits [splits]
  (let [s (merge default-splits splits)]
    {:col (clamp (:col s) 0.35 0.80)
     :left (clamp (:left s) 0.25 0.80)
     :right (clamp (:right s) 0.25 0.75)}))

(defn default-layout [panel-ids]
  {:order (vec panel-ids)
   :hidden #{}
   :focus-panel :vehicles
   :secondary-panel (get focus->default-secondary :vehicles :staging)
   :splits default-splits})

(defn toggle-hidden [layout panel-id]
  (update layout :hidden (fn [hidden]
                           (if (contains? hidden panel-id)
                             (disj hidden panel-id)
                             (conj (or hidden #{}) panel-id)))))

(defn move-panel [layout panel-id dir]
  (let [order (:order layout)
        idx (.indexOf order panel-id)
        target (+ idx dir)]
    (if (or (neg? idx)
            (neg? target)
            (>= target (count order)))
      layout
      (assoc layout :order (-> order
                               (assoc idx (nth order target))
                               (assoc target panel-id))))))

(defn visible-order [{:keys [order hidden]}]
  (vec (remove hidden order)))

(def focus->label
  {:vehicles "Vehicles"
   :transports "Transports"
   :map "Map"})

(defn selectable-focus-panels []
  [:vehicles :transports :map])

(defn update-focus-panel [layout panel-id]
  (assoc layout :focus-panel panel-id))

(defn update-split [layout split-k ratio]
  (update layout :splits (fn [splits]
                           (-> (merge default-splits splits)
                               (assoc split-k ratio)
                               clamp-splits))))

(defn reset-splits-for-focus [layout focus-panel-id]
  (-> layout
      (assoc :focus-panel focus-panel-id)
      (assoc :secondary-panel (get focus->default-secondary focus-panel-id (:secondary-panel layout)))
      (assoc :splits (get focus->default-splits focus-panel-id default-splits))))

(defn pct [x]
  (str (* 100 x) "%"))

(defn rect-style [{:keys [x y w h]}]
  {:left (str "calc(" (pct x) " + 4px)")
   :top (str "calc(" (pct y) " + 4px)")
   :width (str "calc(" (pct w) " - 8px)")
   :height (str "calc(" (pct h) " - 8px)")})

(defn adaptive-plan
  [{:keys [visible focus-panel-id secondary-panel-id splits]}]
  (let [panel-count (count visible)
        focus-id (or (some #{focus-panel-id} visible) (first visible))
        remaining (vec (remove #{focus-id} visible))
        {:keys [col left right]} (clamp-splits splits)]
    (cond
      (<= panel-count 0)
      {:panel-rects {}
       :splitters {}}

      (= panel-count 1)
      {:panel-rects {focus-id {:x 0 :y 0 :w 1 :h 1}}
       :splitters {}}

      (= panel-count 2)
      (let [[other-id] remaining]
        {:panel-rects {focus-id {:x 0 :y 0 :w col :h 1}
                       other-id {:x col :y 0 :w (- 1 col) :h 1}}
         :splitters {:col {:x col :active? true}}})

      :else
      (let [left-bottom-id (if (and secondary-panel-id
                                    (not= secondary-panel-id focus-id)
                                    (some #{secondary-panel-id} remaining))
                             secondary-panel-id
                             (first remaining))
            right-panels (vec (remove #{left-bottom-id} remaining))
            right-staging? (and (= 2 (count right-panels))
                                (some #{:staging} right-panels))
            right-top-id (if right-staging?
                           (first (remove #{:staging} right-panels))
                           (first right-panels))
            right-bottom-id (if right-staging?
                              :staging
                              (second right-panels))
            left-top-h (if left-bottom-id left 1)
            right-top-h (if right-bottom-id right 1)]
        {:panel-rects (cond-> {focus-id {:x 0 :y 0 :w col :h left-top-h}}
                        left-bottom-id (assoc left-bottom-id {:x 0 :y left-top-h :w col :h (- 1 left-top-h)})
                        right-top-id (assoc right-top-id {:x col :y 0 :w (- 1 col) :h right-top-h})
                        right-bottom-id (assoc right-bottom-id {:x col :y right-top-h :w (- 1 col) :h (- 1 right-top-h)}))
         :splitters (cond-> {:col {:x col :active? true}}
                      left-bottom-id (assoc :left {:x0 0 :x1 col :y left-top-h :active? true})
                      right-bottom-id (assoc :right {:x0 col :x1 1 :y right-top-h :active? true}))}))))

(defn swap-order [layout src-panel-id dst-panel-id]
  (let [order (:order layout)
        src-idx (.indexOf order src-panel-id)
        dst-idx (.indexOf order dst-panel-id)]
    (if (or (neg? src-idx) (neg? dst-idx) (= src-idx dst-idx))
      layout
      (assoc layout :order (-> order
                               (assoc src-idx (nth order dst-idx))
                               (assoc dst-idx src-panel-id))))))

(defn overlap-ratio
  "Returns area-overlap ratio between virtual dragged rect and target rect."
  [{:keys [x y w h]} ^js target-el]
  (let [r (.getBoundingClientRect target-el)
        left (max x (.-left r))
        right (min (+ x w) (.-right r))
        top (max y (.-top r))
        bottom (min (+ y h) (.-bottom r))
        overlap-w (max 0 (- right left))
        overlap-h (max 0 (- bottom top))
        overlap-area (* overlap-w overlap-h)
        target-area (* (.-width r) (.-height r))]
    (if (pos? target-area)
      (/ overlap-area target-area)
      0)))

(defn point-in-rect? [x y ^js rect]
  (and (<= (.-left rect) x (.-right rect))
       (<= (.-top rect) y (.-bottom rect))))

(defn dnd-debug-enabled? []
  (boolean (.-__lbv2DndDebug js/window)))

(defn panel-root-el [panel-id]
  (.querySelector js/document (str ".paneling-panel-root[data-panel-id=\"" (name panel-id) "\"]")))

(defn drag-source-id [event]
  (let [raw (some-> event .-dataTransfer (.getData "text/plain"))]
    (when (and raw (not= "" raw))
      (keyword raw))))

(defn panel-card [{:keys [panel-id title header-right on-popout child on-drag-start on-drag-end on-header-drag-event
                          dragging? drop-candidate? rounded?]
                   :or {rounded? true}}]
  [:div.flex.flex-col.border.border-slate-300.bg-white.shadow-sm.overflow-hidden.h-full.min-w-0.min-h-0
   {:data-testid (str "panel-" (name panel-id))
    :class (cond-> []
             rounded? (conj "rounded")
             dragging? (conj "opacity-70")
             drop-candidate? (conj "ring-2 ring-indigo-500 ring-offset-2"))}
   [:div.flex.items-center.gap-2.border-b.border-slate-200.bg-slate-50.px-2.cursor-move.select-none.whitespace-nowrap.overflow-hidden
    {:class "h-[26px] min-h-[26px] shrink-0"
     :draggable true
     :on-drag-start (fn [event]
                      (let [panel-el (or (panel-root-el panel-id)
                                         (.closest (.-currentTarget event) ".paneling-panel-root"))
                            rect (.getBoundingClientRect panel-el)]
                        (when on-header-drag-event
                          (on-header-drag-event :dragstart panel-id event))
                        (set! (.. event -dataTransfer -effectAllowed) "move")
                        (.. event -dataTransfer (setData "text/plain" (name panel-id)))
                        ;; Use a cloned drag ghost to avoid browser choosing a parent container
                        ;; as drag image for complex grid layouts.
                        (let [ghost (.cloneNode panel-el true)]
                          (set! (.. ghost -style -position) "fixed")
                          (set! (.. ghost -style -left) "-10000px")
                          (set! (.. ghost -style -top) "-10000px")
                          (set! (.. ghost -style -width) (str (.-width rect) "px"))
                          (set! (.. ghost -style -height) (str (.-height rect) "px"))
                          (set! (.. ghost -style -margin) "0")
                          (set! (.. ghost -style -pointerEvents) "none")
                          (.appendChild (.-body js/document) ghost)
                          (.. event -dataTransfer (setDragImage ghost
                                                                (max 0 (- (.-clientX event) (.-left rect)))
                                                                (max 0 (- (.-clientY event) (.-top rect)))))
                          (js/setTimeout #(.remove ghost) 0))
                        (when on-drag-start (on-drag-start panel-id event panel-el rect))))
     :on-drag-over #(.preventDefault %)
     :on-drag-end (fn [event]
                    (when on-header-drag-event
                      (on-header-drag-event :dragend panel-id event))
                    (when on-drag-end (on-drag-end panel-id event)))}
    [:div.font-bold.min-w-0.truncate.flex.items-center {:class "text-[12px]"} title]
    [:div.ml-auto.flex.items-center.justify-end.gap-2.min-w-0.text-slate-600.overflow-hidden
     {:class "text-[12px]"}
     (when header-right
       header-right)
     (when on-popout
       [:button.bg-transparent.border-0.p-0.m-0.leading-none
        {:data-testid (str "popout-" (name panel-id))
         :class "text-slate-500 hover:text-slate-700"
         :on-click #(on-popout panel-id)}
        [ui/icon {:size 12} :open-in-new-window]])]]
   [:div.flex-auto.min-h-0 child]])

(defn layout-grid [{:keys [layout panel-meta panel->view on-popout on-drop-panel exclude-panel-ids on-layout-change]}]
  (let [excluded (or exclude-panel-ids #{})
        visible (vec (remove excluded (visible-order layout)))
        focus-panel-id (or (:focus-panel layout) :vehicles)
        secondary-panel-id (or (:secondary-panel layout) :staging)
        layout-splits (clamp-splits (:splits layout))
        {:keys [panel-rects splitters]} (adaptive-plan {:visible visible
                                                        :focus-panel-id focus-panel-id
                                                        :secondary-panel-id secondary-panel-id
                                                        :splits layout-splits})
        !container-ref (hooks/use-ref nil)
        !on-layout-change-ref (hooks/use-ref on-layout-change)
        !drag (hooks/use-state nil)
        !candidate-id (hooks/use-state nil)
        !swap-committed? (hooks/use-state false)
        !resizing (hooks/use-state nil)
        !raf (hooks/use-ref nil)
        !latest-point (hooks/use-ref nil)
        !debug (hooks/use-state {:events []
                                 :boxes []
                                 :virtual-rect nil
                                 :best-ratio 0
                                 :last-commit nil
                                 :session-id nil})
        threshold 0.22
        log! (fn [kind payload]
               (when (dnd-debug-enabled?)
                 (swap! !debug (fn [d]
                                 (-> d
                                     (update :events (fn [events]
                                                       (->> (conj (vec events)
                                                                  (assoc payload
                                                                         :kind kind
                                                                         :t (js/Math.round (.now js/performance))))
                                                            (take-last 80)
                                                            vec))))))))
        panel-roots (fn []
                      (array-seq (.querySelectorAll js/document ".paneling-panel-root[data-panel-id]")))
        compute-target-candidate (fn [drag target-panel-id target-el x y]
                                   (let [{:keys [panel-id w h offset-x offset-y]} drag
                                         virtual-rect {:x (- x offset-x)
                                                       :y (- y offset-y)
                                                       :w w
                                                       :h h}
                                         target-rect (when target-el (.getBoundingClientRect target-el))
                                         target-ratio (when target-el
                                                        (overlap-ratio virtual-rect target-el))
                                         inside-target? (when target-rect
                                                          (point-in-rect? x y target-rect))
                                         candidate-id (when (and target-panel-id
                                                                 (not= target-panel-id panel-id)
                                                                 inside-target?
                                                                 target-ratio
                                                                 (>= target-ratio threshold))
                                                        target-panel-id)
                                         boxes (->> (panel-roots)
                                                    (map (fn [^js el]
                                                           (let [id (keyword (.. el -dataset -panelId))]
                                                             {:id id
                                                              :ratio (if (= id panel-id)
                                                                       0
                                                                       (overlap-ratio virtual-rect el))
                                                              :rect (.getBoundingClientRect el)}))))]
                                     {:virtual-rect virtual-rect
                                      :boxes boxes
                                      :best-ratio (or target-ratio 0)
                                      :candidate-id candidate-id}))
        update-candidate! (fn [event target-panel-id]
                            (when (and @!drag (not @!resizing))
                              (let [drag @!drag
                                    _ (when (.-dataTransfer event)
                                        (set! (.. event -dataTransfer -dropEffect) "move"))
                                    target-el (some-> (js/document.elementFromPoint (.-clientX event) (.-clientY event))
                                                      (.closest ".paneling-panel-root[data-panel-id]"))
                                    hovered-panel-id (some-> target-el
                                                             (.-dataset)
                                                             (aget "panelId")
                                                             keyword)
                                    target-id (if (= target-panel-id :window)
                                                hovered-panel-id
                                                (or target-panel-id hovered-panel-id))
                                    {:keys [virtual-rect boxes best-ratio candidate-id]}
                                    (compute-target-candidate drag target-id target-el (.-clientX event) (.-clientY event))
                                    payload {:target-panel-id target-panel-id
                                             :source-panel-id (:panel-id drag)
                                             :candidate-id candidate-id
                                             :best-ratio (.toFixed (double best-ratio) 3)
                                             :x (.-clientX event)
                                             :y (.-clientY event)}]
                                (reset! !candidate-id candidate-id)
                                (when (dnd-debug-enabled?)
                                  (swap! !debug assoc
                                         :virtual-rect virtual-rect
                                         :boxes boxes
                                         :best-ratio best-ratio))
                                (log! :dragover payload))))
        on-drag-start* (fn [panel-id event panel-el rect]
                         (when-not @!resizing
                           (let [session-id (str (random-uuid))]
                             (swap! !debug assoc
                                    :session-id session-id
                                    :events []
                                    :last-commit nil
                                    :boxes []
                                    :virtual-rect nil
                                    :best-ratio 0)
                             (log! :dragstart {:source-panel-id panel-id
                                               :session-id session-id
                                               :x (.-clientX event)
                                               :y (.-clientY event)}))
                           (reset! !swap-committed? false)
                           (reset! !candidate-id nil)
                           (reset! !drag {:panel-id panel-id
                                          :w (.-width rect)
                                          :h (.-height rect)
                                          :offset-x (- (.-clientX event) (.-left rect))
                                          :offset-y (- (.-clientY event) (.-top rect))})))
        on-drag-over* (fn [target-panel-id event]
                        (when-not @!resizing
                          (update-candidate! event target-panel-id)))
        commit-swap! (fn [reason src-panel-id-override dst-panel-id-override]
                       (when (and (not @!swap-committed?) (not @!resizing))
                         (let [src-panel-id (or src-panel-id-override
                                                (:panel-id @!drag))
                               dst-panel-id (or dst-panel-id-override @!candidate-id)]
                           (if (and src-panel-id
                                    dst-panel-id
                                    (not= src-panel-id dst-panel-id)
                                    on-drop-panel)
                             (do
                               (reset! !swap-committed? true)
                               (swap! !debug assoc :last-commit {:reason reason
                                                                 :src src-panel-id
                                                                 :dst dst-panel-id})
                               (log! :commit {:reason reason
                                              :src src-panel-id
                                              :dst dst-panel-id})
                               (on-drop-panel src-panel-id dst-panel-id))
                             (log! :commit-skip {:reason reason
                                                 :src src-panel-id
                                                 :dst dst-panel-id
                                                 :has-src? (boolean src-panel-id)
                                                 :has-dst? (boolean dst-panel-id)
                                                 :same? (= src-panel-id dst-panel-id)
                                                 :has-drop-handler? (boolean on-drop-panel)})))))
        on-drop* (fn [target-panel-id event]
                   (when event (.preventDefault event))
                   (when event (.stopPropagation event))
                   (log! :drop {:target-panel-id target-panel-id
                                :candidate-id @!candidate-id
                                :source-panel-id (:panel-id @!drag)
                                :source-override (drag-source-id event)})
                   (commit-swap! :panel-drop (drag-source-id event) target-panel-id)
                   (reset! !drag nil)
                   (reset! !candidate-id nil))
        on-drag-end* (fn [panel-id event]
                       (log! :dragend {:source-panel-id panel-id
                                       :candidate-id @!candidate-id
                                       :x (.-clientX event)
                                       :y (.-clientY event)})
                       nil)
        on-header-drag-event* (fn [kind panel-id event]
                                (log! kind {:panel-id panel-id
                                            :x (.-clientX event)
                                            :y (.-clientY event)}))
        start-resize! (fn [kind event]
                        (when (and on-layout-change
                                   (not @!drag))
                          (.preventDefault event)
                          (.stopPropagation event)
                          (when (.-currentTarget event)
                            (try
                              (.setPointerCapture (.-currentTarget event) (.-pointerId event))
                              (catch :default _ nil)))
                          (when-let [el @!container-ref]
                            (let [rect (.getBoundingClientRect el)]
                              (set! (.. js/document -body -style -userSelect) "none")
                              (set! (.. js/document -body -style -cursor)
                                    (if (= kind :col) "col-resize" "row-resize"))
                              (reset! !candidate-id nil)
                              (reset! !resizing {:kind kind
                                                 :start-x (.-clientX event)
                                                 :start-y (.-clientY event)
                                                 :start-splits layout-splits
                                                 :container-rect rect})))))]
    (set! (.-current !on-layout-change-ref) on-layout-change)
    (hooks/use-effect
     (fn []
       (let [clear-drag! (fn [_]
                           (reset! !drag nil)
                           (reset! !candidate-id nil))
             on-window-dragover (fn [event]
                                  (when @!drag
                                    (.preventDefault event)
                                    (when (.-dataTransfer event)
                                      (set! (.. event -dataTransfer -dropEffect) "move"))
                                    (update-candidate! event :window)))
             on-window-drop (fn [event]
                              (.preventDefault event)
                              (when (.-dataTransfer event)
                                (set! (.. event -dataTransfer -dropEffect) "move"))
                              (let [target-el (some-> (js/document.elementFromPoint (.-clientX event) (.-clientY event))
                                                      (.closest ".paneling-panel-root[data-panel-id]"))
                                    target-panel-id (some-> target-el
                                                            (.-dataset)
                                                            (aget "panelId")
                                                            keyword)
                                    source-panel-id (drag-source-id event)]
                                (log! :window-drop {:candidate-id @!candidate-id
                                                    :source-panel-id (:panel-id @!drag)
                                                    :source-override source-panel-id
                                                    :target-override target-panel-id
                                                    :x (.-clientX event)
                                                    :y (.-clientY event)})
                                ;; If nested content swallows panel-root drop,
                                ;; still commit swap against highlighted candidate.
                                (commit-swap! :window-drop source-panel-id target-panel-id)
                                (clear-drag! nil)))]
         (js/window.addEventListener "dragover" on-window-dragover true)
         (js/window.addEventListener "drop" on-window-drop true)
         #(do
            (js/window.removeEventListener "dragover" on-window-dragover true)
            (js/window.removeEventListener "drop" on-window-drop true))))
     [])
    (hooks/use-effect
     (fn []
       (when-let [resizing @!resizing]
         (let [apply-resize! (fn [x y]
                               (let [{:keys [kind start-x start-y start-splits container-rect]} resizing
                                     w (max 1 (.-width container-rect))
                                     h (max 1 (.-height container-rect))
                                     dx (- x start-x)
                                     dy (- y start-y)
                                     ratio (case kind
                                             :col (+ (:col start-splits) (/ dx w))
                                             :left (+ (:left start-splits) (/ dy h))
                                             :right (+ (:right start-splits) (/ dy h))
                                             nil)]
                                 (when ratio
                                   (when-let [on-layout-change* (.-current !on-layout-change-ref)]
                                     (on-layout-change* #(-> %
                                                             (assoc :splits start-splits)
                                                             (update-split kind ratio)))))))
               on-move (fn [event]
                         (set! (.-current !latest-point) {:x (.-clientX event)
                                                          :y (.-clientY event)})
                         (when-not (.-current !raf)
                           (set! (.-current !raf)
                                 (js/requestAnimationFrame
                                  (fn []
                                    (set! (.-current !raf) nil)
                                    (when-let [{:keys [x y]} (.-current !latest-point)]
                                      (apply-resize! x y)))))))
               stop! (fn []
                       (when-let [raf-id (.-current !raf)]
                         (js/cancelAnimationFrame raf-id)
                         (set! (.-current !raf) nil))
                       (set! (.. js/document -body -style -userSelect) "")
                       (set! (.. js/document -body -style -cursor) "")
                       (reset! !resizing nil))
               on-up (fn [_] (stop!))]
           (js/window.addEventListener "pointermove" on-move)
           (js/window.addEventListener "pointerup" on-up)
           (js/window.addEventListener "pointercancel" on-up)
           #(do
              (js/window.removeEventListener "pointermove" on-move)
              (js/window.removeEventListener "pointerup" on-up)
              (js/window.removeEventListener "pointercancel" on-up)
              (stop!)))))
     [@!resizing])
    [:<>
     [:div.relative.h-full.w-full {:ref !container-ref}
      (into
       [:<>]
       (for [panel-id visible
             :let [{:keys [title header-right popout-enabled?]} (get panel-meta panel-id)]]
         ^{:key (str "panel-" (name panel-id))}
         [:div.paneling-panel-root.absolute.min-w-0.min-h-0
          {:data-panel-id (name panel-id)
           :style (rect-style (get panel-rects panel-id))
           :on-drag-over #(do (.preventDefault %)
                              (on-drag-over* panel-id %))
           :on-drop #(on-drop* panel-id %)
           :on-drag-enter #(log! :dragenter {:target-panel-id panel-id
                                             :candidate-id @!candidate-id})
           :on-drag-leave #(log! :dragleave {:target-panel-id panel-id
                                             :candidate-id @!candidate-id})}
          [panel-card {:panel-id panel-id
                       :title title
                       :header-right header-right
                       :on-popout (when (and on-popout (not= false popout-enabled?))
                                    on-popout)
                       :on-drag-start on-drag-start*
                       :on-drag-end on-drag-end*
                       :on-header-drag-event on-header-drag-event*
                       :dragging? (= panel-id (:panel-id @!drag))
                       :drop-candidate? (and (not @!resizing)
                                             (= panel-id @!candidate-id))
                       :child (panel->view panel-id)}]]))
      (when (get-in splitters [:col :active?])
        [:div.absolute.top-0.bottom-0.z-30
         {:style {:left (pct (get-in splitters [:col :x]))
                  :transform "translateX(-50%)"
                  :width "1px"}}
         [:div
          {:class "absolute left-1/2 top-0 bottom-0 w-px"
           :style {:transform "translateX(-50%)"}}]
         [:div.absolute.top-0.bottom-0.bg-transparent
          {:style {:left "-5px"
                   :width "10px"
                   :cursor "col-resize"}
           :on-pointer-down #(start-resize! :col %)}]])
      (when (get-in splitters [:left :active?])
        [:div.absolute.z-30
         {:style {:left (pct (get-in splitters [:left :x0]))
                  :width (pct (- (get-in splitters [:left :x1])
                                 (get-in splitters [:left :x0])))
                  :top (pct (get-in splitters [:left :y]))
                  :transform "translateY(-50%)"
                  :height "1px"}}
         [:div
          {:class "absolute top-1/2 left-0 right-0 h-px"
           :style {:transform "translateY(-50%)"}}]
         [:div.absolute.left-0.right-0.bg-transparent
          {:style {:top "-5px"
                   :height "10px"
                   :cursor "row-resize"}
           :on-pointer-down #(start-resize! :left %)}]])
      (when (get-in splitters [:right :active?])
        [:div.absolute.z-30
         {:style {:left (pct (get-in splitters [:right :x0]))
                  :width (pct (- (get-in splitters [:right :x1])
                                 (get-in splitters [:right :x0])))
                  :top (pct (get-in splitters [:right :y]))
                  :transform "translateY(-50%)"
                  :height "1px"}}
         [:div
          {:class "absolute top-1/2 left-0 right-0 h-px"
           :style {:transform "translateY(-50%)"}}]
         [:div.absolute.left-0.right-0.bg-transparent
          {:style {:top "-5px"
                   :height "10px"
                   :cursor "row-resize"}
           :on-pointer-down #(start-resize! :right %)}]])]
     (when (dnd-debug-enabled?)
       [:div.hidden])]))
